// The opponent in a versus match, as seen locally.
//
// Drawn with a real MechView driven by the opponent's ~20 Hz snapshots (smoothed), and
// registered with the enemy manager as an EnemyApi so every local weapon, explosion,
// chain, status and ability can hit it. Damage is SHOOTER-authoritative: receiveDamage
// never changes anything locally — it sends a 'hit' to the opponent, whose game applies
// it to their own player (with their i-frames, shield and armour) and reports the result
// back in its next snapshot.

import Phaser from 'phaser';
import { DEPTH, PLAYER_BODY } from '../../config/constants';
import { COLORS } from '../../config/palette';
import type { EnemyApi, GameContext, HitArea } from '../../core/contracts';
import type { AnimKey, DamageInfo, DamageResult, MechId, StatusId, StatusState, WeaponId } from '../../core/types';
import { ANIM_KEYS } from '../../core/types';
import { MECHS } from '../../data/mechs';
import type { HitMsg, StateMsg } from '../../net/versus';
import { MechView } from '../player/MechView';

let nextUid = 0x7e000;
/** Snap instead of smoothing when the remote position jumps this far (px). */
const SNAP_DIST = 220;
/** Position smoothing rate (1/s). */
const SMOOTH = 18;

export class RivalMech implements EnemyApi {
  readonly uid = nextUid++;
  readonly defId = 'rival' as const;
  readonly kind = 'enemy' as const;
  readonly name: string;
  readonly elite = [] as const;
  readonly obj: Phaser.GameObjects.Zone;
  readonly body: Phaser.Physics.Arcade.Body;
  readonly radius = PLAYER_BODY.h / 2;
  hp: number;
  maxHp: number;
  armorHp = 0;
  readonly spawning = false;
  readonly flying = false;
  readonly statuses = new Map<StatusId, StatusState>();
  readonly knockbackResist = 1;
  readonly scrapValue = 0;

  readonly view: MechView;
  /** Follow snapshots exactly (local AI pilot) instead of smoothing network ones. */
  exact = false;
  shield = 0;
  maxShield = 0;
  /** Latest snapshot (null until the first one arrives). */
  private snap: StateMsg | null = null;
  private fx = 0;
  private fy = 0;
  private weapon: string | null = null;
  private equipKey = '';
  private dead = false;
  private readonly areas: HitArea[] = [{ x: 0, y: 0, w: PLAYER_BODY.w, h: PLAYER_BODY.h, mult: 1 }];

  constructor(
    private readonly ctx: GameContext,
    readonly mechId: MechId,
    playerName: string,
    x: number,
    feetY: number,
    private readonly sendHit: (h: HitMsg) => void,
  ) {
    const scene = ctx.scene;
    this.name = playerName.toUpperCase();
    const def = MECHS[mechId] ?? MECHS.volt;
    this.hp = this.maxHp = def.base.maxHp;
    this.fx = x;
    this.fy = feetY;
    this.obj = scene.add.zone(x, feetY - PLAYER_BODY.h / 2, PLAYER_BODY.w, PLAYER_BODY.h);
    scene.physics.add.existing(this.obj, false);
    this.body = this.obj.body as Phaser.Physics.Arcade.Body;
    this.body.setAllowGravity(false);
    this.body.moves = false;
    this.view = new MechView(scene, mechId);
    this.view.setDepth(DEPTH.PLAYER - 1);
    this.view.setReactorColor(def.reactorColor);
    this.view.setWeapon(def.startingWeapon);
    this.view.setPosition(x, feetY);
    this.view.setFacing(x > ctx.world.width / 2 ? -1 : 1);
    this.view.play('idle');
  }

  get x(): number {
    return this.fx;
  }

  /** Centre. */
  get y(): number {
    return this.fy - PLAYER_BODY.h / 2;
  }

  get alive(): boolean {
    return !this.dead;
  }

  get hasState(): boolean {
    return this.snap !== null;
  }

  hitAreas(): readonly HitArea[] {
    const a = this.areas[0];
    a.x = this.fx - PLAYER_BODY.w / 2;
    a.y = this.fy - PLAYER_BODY.h;
    return this.areas;
  }

  receiveDamage(amount: number, info: DamageInfo): DamageResult {
    if (this.dead || !(amount > 0)) return { dealt: 0, killed: false, blocked: false, absorbed: 0 };
    this.sendHit({
      t: 'hit',
      a: Math.round(amount * 10) / 10,
      tags: (info.tags ?? []).slice(0, 6),
      sx: info.sourceX !== undefined ? Math.round(info.sourceX) : undefined,
      sy: info.sourceY !== undefined ? Math.round(info.sourceY) : undefined,
      kb: info.knockback ? Math.round(info.knockback) : undefined,
      crit: info.crit || undefined,
      dot: info.isDot || undefined,
      label: info.label,
    });
    if (!info.isDot) this.view.flash(0xffffff, 70);
    // Predicted locally for the bar; the next snapshot corrects it.
    const absorbed = Math.min(this.shield, amount);
    this.shield -= absorbed;
    const dealt = Math.min(this.hp, amount - absorbed);
    this.hp = Math.max(1, this.hp - dealt);
    return { dealt, killed: false, blocked: false, absorbed };
  }

  knockback(): void {
    // Applied on the opponent's side (the hit message carries the knockback).
  }

  pull(): void {
    // Gravity fields act on the opponent's own simulation.
  }

  /** New snapshot from the network. */
  apply(s: StateMsg): void {
    const first = !this.snap;
    this.snap = s;
    this.hp = s.hp;
    this.maxHp = s.maxHp;
    this.shield = s.sh;
    this.maxShield = s.maxSh;
    if (first || Math.hypot(s.x - this.fx, s.y - this.fy) > SNAP_DIST) {
      this.fx = s.x;
      this.fy = s.y;
    }
    if (s.weapon !== this.weapon) {
      this.weapon = s.weapon;
      this.view.setWeapon((s.weapon as WeaponId) ?? null);
    }
    if (s.equip) {
      const k = s.equip.join(',');
      if (k !== this.equipKey) {
        this.equipKey = k;
        this.view.setEquipment(s.equip);
      }
    }
    if (!s.alive && !this.dead) this.die();
    else if (s.alive && this.dead) this.revive();
  }

  /** The opponent fired: kick the arms (the shot itself is mirrored as a ghost projectile). */
  recoil(): void {
    this.view.recoil(0.7);
  }

  /** Alive again (a stale 'dead' snapshot got through): back to normal tracking. */
  private revive(): void {
    this.dead = false;
    this.view.play('idle', { restart: true });
  }

  private die(): void {
    this.dead = true;
    this.view.setSpin(0, 0);
    this.view.play('death', { restart: true });
    try {
      this.ctx.fx.explosion(this.fx, this.y, 70, COLORS.threatHot);
      this.ctx.fx.debris(this.fx, this.y, { count: 12, speed: 380 });
    } catch {
      // Cosmetic only.
    }
  }

  update(dt: number): void {
    const s = this.snap;
    if (s && !this.dead) {
      // Extrapolate a little along the reported velocity, then ease toward it.
      const tx = s.x + s.vx * 0.05;
      const ty = s.y + s.vy * 0.05;
      const k = this.exact ? 1 : 1 - Math.exp(-dt * SMOOTH);
      this.fx += (tx - this.fx) * k;
      this.fy += (ty - this.fy) * k;
      this.view.setFacing(s.f);
      this.view.setAim(s.aim);
      const anim = (ANIM_KEYS as readonly string[]).includes(s.anim) ? (s.anim as AnimKey) : 'idle';
      this.view.setPhase(anim, s.fc > 0 ? (s.fr + 0.5) / s.fc : 0);
      this.view.setSpin(s.spin, s.tuck);
    }
    this.obj.setPosition(this.fx, this.y);
    this.view.setPosition(this.fx, this.fy);
    this.view.update(dt);
  }

  destroy(): void {
    this.view.destroy();
    if (this.obj.active) this.obj.destroy();
  }
}
